// This is used for Work Assignments and Actions to record whether the user navigated to the view record from the kanban or table, for a more intuitive "back" experience.
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

export type ViewType = 'table' | 'kanban' | 'timeline';

const VIEW_TYPES: readonly ViewType[] = ['table', 'kanban', 'timeline'];

function isViewType(value: string | null): value is ViewType {
  return VIEW_TYPES.includes(value as ViewType);
}

interface UseViewStateOptions {
  defaultView?: ViewType;
  storageKey?: string;
}

export const useViewState = (options: UseViewStateOptions = {}) => {
  const { defaultView = 'table', storageKey = 'default-view' } = options;
  const location = useLocation();
  const navigate = useNavigate();

  // Get view from URL params first, then localStorage, then default
  const getInitialView = (): ViewType => {
    const urlParams = new URLSearchParams(location.search);
    const urlView = urlParams.get('view') as ViewType;
    if (isViewType(urlView)) {
      return urlView;
    }

    const storedView = localStorage.getItem(storageKey);
    if (isViewType(storedView)) {
      return storedView;
    }

    return defaultView;
  };

  const [activeView, setActiveViewState] = useState<ViewType>(getInitialView);

  // Update URL and localStorage when view changes
  const setActiveView = (view: string) => {
    // Type guard to ensure view is a valid ViewType
    if (!isViewType(view)) {
      return;
    }

    setActiveViewState(view);

    // Update localStorage
    localStorage.setItem(storageKey, view);

    // Update URL params
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('view', view);
    const newSearch = urlParams.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

    // Only navigate if the URL actually changed
    if (newUrl !== location.pathname + location.search) {
      navigate(newUrl, { replace: true });
    }
  };

  // Sync with URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlView = urlParams.get('view');
    if (isViewType(urlView) && urlView !== activeView) {
      setActiveViewState(urlView);
      localStorage.setItem(storageKey, urlView);
    }
  }, [location.search, activeView, storageKey]);

  return {
    activeView,
    setActiveView,
  };
};
